export const polygonTableSVGElement = ({
  text = 'Heap',
  volumeCut,
  volumeFill,
  volumeNet,
  weightCut,
  weightFill,
  weightNet,
}) => {
  // Handlers.
  const getTextWidth = (text: string, font: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return 0;
    }
    context.font = font;
    const metrics = context.measureText(text);
    canvas.remove();
    return metrics.width;
  };

  const getMaxCharsForWidth = (widthPx: number, font: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return 0;
    }
    context.font = font;

    let totalWidth = 0;
    let count = 0;

    for (const char of text) {
      const charWidth = context.measureText(char).width;
      if (totalWidth + charWidth > widthPx) {
        break;
      }
      totalWidth += charWidth;
      count++;
    }

    return count;
  };

  const createTextElement = (
    x: string | number,
    y: string | number,
    fontSize: number,
    fontWeight: string | number,
    fill: string,
    text: string,
  ) => `
  <text x="${x}" y="${y}" font-family="${labelOptions.fontFamily}" font-size="${fontSize}" font-weight="${fontWeight}" fill="${fill}">
    ${text}
  </text>
`;

  const getTextAlignmentPercentage = (text: string) => {
    return text === '-' ? '60%' : '50%';
  };

  // Constants.
  const labelOptions = {
    fontSize: 10,
    fontWeight: 500,
    fontFamily: 'Rubik',
    textColor: '#000000',
    backgroundColor: '#ffcc33',
    strokeColor: '#3A3A3A',
    strokeWidth: 1,
    borderRadius: 6,
    paddingHorizontal: 4,
    opacity: 1,
  };

  const values = [
    volumeCut,
    volumeFill,
    volumeNet,
    weightCut,
    weightFill,
    weightNet,
  ];
  const maxTextWidth = Math.max(
    ...values.map((value) => getTextWidth(`${value}`, '10px Rubik') || 0),
  );

  // Assigned 190 as it's the minimum width required for showing info details.
  const tablePadding = 190;
  const fontSpec = '16px Roman';

  const maxTableWidth = maxTextWidth + tablePadding;
  const maxHeaderWidth = maxTableWidth * 0.75;

  // Removing 20px from maxHeaderWidth to account for three dots.
  const maxHeaderTextLength = getMaxCharsForWidth(
    maxHeaderWidth - 20,
    fontSpec,
  );

  const renderedTextWidth = getTextWidth(text, fontSpec);

  const headerText =
    renderedTextWidth > maxHeaderWidth
      ? `${text.slice(0, maxHeaderTextLength)}...`
      : text;

  return `
    <svg xmlns="http://www.w3.org/2000/svg" width="${maxTableWidth + labelOptions.paddingHorizontal}" height="200">
      
      <!-- Header rectangle -->
      <rect x="6.8%" y="6" rx="${labelOptions.borderRadius}" ry="${labelOptions.borderRadius}" width="85%" height="35"
        style="fill:${labelOptions.strokeColor};" />

      <!-- Main yellow rectangle -->
      <rect x="4" y="30" width="${maxTableWidth - 10}" height="165" 
        fill="${labelOptions.backgroundColor}"
        stroke="${labelOptions.strokeColor}"
        stroke-width="${labelOptions.strokeWidth}"
        rx="${labelOptions.borderRadius}" 
        ry="${labelOptions.borderRadius}" />

      <!-- Title in header (centered) -->
      ${createTextElement('50%', 23, 16, 'bold', 'white', headerText).replace(
        '<text',
        '<text text-anchor="middle"',
      )}
      
      <!-- Table structure -->
      <g transform="translate(15, 60)">
        <!-- Volume row -->
        ${createTextElement(0, 10, 18, 'bold', '#333', 'Volume')}
        ${createTextElement(17, 30, 16, 'normal', '#333', '(m³)')}
        
        <!-- Vertical "Cut Fill Net" -->
        ${createTextElement(70, 0, 18, 'bold', '#333', 'Cut')}
        ${createTextElement(70, 22, 18, 'bold', '#333', 'Fill')}
        ${createTextElement(70, 44, 18, 'bold', '#333', 'Net')}
        
        ${createTextElement(`${getTextAlignmentPercentage(volumeCut)}`, 0, 18, 'bold', '#333', volumeCut)}
        ${createTextElement(`${getTextAlignmentPercentage(volumeFill)}`, 22, 18, 'bold', '#333', volumeFill)}
        ${createTextElement(`${getTextAlignmentPercentage(volumeNet)}`, 44, 18, 'bold', '#333', volumeNet)}
        
        <!-- Weight row -->
        ${createTextElement(0, 90, 18, 'bold', '#333', 'Weight')}
        ${createTextElement(2, 110, 16, 'normal', '#333', '(tonnes)')}
        
        <!-- Vertical "Cut Fill Net" for weight -->
        ${createTextElement(70, 80, 18, 'bold', '#333', 'Cut')}
        ${createTextElement(70, 102, 18, 'bold', '#333', 'Fill')}
        ${createTextElement(70, 124, 18, 'bold', '#333', 'Net')}
        
        ${createTextElement(`${getTextAlignmentPercentage(weightCut)}`, 80, 18, 'bold', '#333', weightCut)}
        ${createTextElement(`${getTextAlignmentPercentage(weightFill)}`, 102, 18, 'bold', '#333', weightFill)}
        ${createTextElement(`${getTextAlignmentPercentage(weightNet)}`, 124, 18, 'bold', '#333', weightNet)}
      </g>
      
      <!-- Horizontal divider line -->
      <line x1="10" y1="115" x2="${maxTableWidth - 10}" y2="115" stroke="#000000" stroke-width="1" />
    </svg>`;
};
